import { Plus32 } from '@packages/themes/icons';
import { useIntl } from 'react-intl';
import {
  Box,
  InlineNotification,
  MuiIconButton,
  Notification,
  Typography,
  useDeviceType,
} from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useState } from 'react';
import { AddRatingFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddRatingFragmentFragmentDoc';
import { useRatingTracking } from '../../Tracking/hooks/useRatingTracking';
import { AddRatingDialog } from './AddRatingDialog';
import { ratingMessages } from './messages';
import { CompactProduct } from '../../CompactProduct';
import { useWriteCustomerRatingEnabled } from '../useWriteCustomerRatingEnabled';

/* GraphQL */ `
  fragment AddRatingFragment on Product {
    ...MultiStepFormFragment
    ...CompactProductFragment
  }
`;

type AddRatingProps = {
  /**
   * ProductData for glycerin submitRating event
   */
  productData: FragmentType<typeof AddRatingFragmentFragmentDoc>;
};

/**
 * Add rating dashed button with multi step form in a dialog
 */
export const AddRating = ({ productData }: AddRatingProps) => {
  const product = getFragmentData(AddRatingFragmentFragmentDoc, productData);
  const [dialogOpened, setDialogOpened] = useState(false);
  const [openSuccessNotification, setOpenSuccessNotification] = useState(false);
  const [openErrorNotification, setOpenErrorNotification] = useState(false);
  const { isMobile } = useDeviceType();
  const { formatMessage } = useIntl();

  const { trackGlycerinButtonClick } = useRatingTracking();
  const writeCustomerRatingEnabled = useWriteCustomerRatingEnabled();

  if (!writeCustomerRatingEnabled) return null;

  const handleSuccess = () => {
    setOpenSuccessNotification(true);
    setDialogOpened(false);
  };

  const handleOpenDialog = ({ category }: { category: string }) => {
    trackGlycerinButtonClick({ category });
    setDialogOpened(true);
  };

  return (
    <>
      <Notification
        open={openSuccessNotification}
        severity="success"
        autoHideDuration={5000}
        onClose={() => setOpenSuccessNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitSuccessNotification)}
      </Notification>
      <Notification
        open={openErrorNotification}
        severity="error"
        autoHideDuration={5000}
        onClose={() => setOpenErrorNotification(false)}
      >
        {formatMessage(ratingMessages.newRatingSubmitErrorNotification)}
      </Notification>
      {dialogOpened && (
        <AddRatingDialog
          open={dialogOpened}
          title={formatMessage(ratingMessages.dialogTitle)}
          handleClose={() => setDialogOpened(false)}
          contentProps={{
            productData: product,
            productOverview: <CompactProduct data={product} withProductName showTaxHint={false} />,
            onSuccess: handleSuccess,
            onError: () => setOpenErrorNotification(true),
          }}
        />
      )}
      {openSuccessNotification && isMobile && (
        <InlineNotification severity="info">
          {formatMessage(ratingMessages.newRatingSubmitSuccess)}
        </InlineNotification>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: '100%',
          pt: 4,
          pb: 3.5,
          border: '1px dashed',
          borderColor: 'grey.main',
          borderRadius: '3px',
        }}
      >
        <MuiIconButton
          clickTrackingProps={null}
          onClick={() => handleOpenDialog({ category: ratingMessages.buttonLabel.defaultMessage })}
          sx={{ bgcolor: 'transparent', border: `1px solid`, borderColor: 'text.darkTransparent' }}
          aria-label={formatMessage(ratingMessages.buttonLabel)}
        >
          <Plus32 sx={{ width: 48, height: 48 }} />
        </MuiIconButton>
        <Typography textAlign="center">{formatMessage(ratingMessages.buttonLabel)}</Typography>
      </Box>
    </>
  );
};
